import {
  type BaseSyntheticEvent,
  type ChangeEvent,
  type FocusEventHandler,
  useEffect,
  useMemo,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import {
  type Control,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useMutationRequest,
  useOrderTicketReviewQuote,
} from "~/v2/api/hooks";
import { useGetMarketStatus } from "~/v2/api/hooks/useGetMarketStatus";
import { invalidateOrderRelatedQueries } from "~/v2/api/invalidateOrderRelatedQueries";
import type { Account, Margin, Position, Product } from "~/v2/api/rest/account";
import type { Asset } from "~/v2/api/rest/assets";
import type { Clock, Quote } from "~/v2/api/rest/data";
import { createOrder } from "~/v2/api/rest/order";
import type {
  OrderType,
  QuantityType,
  Side,
  TimeInForce,
  TrailType,
} from "~/v2/constants/equityOrder";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { shouldUseTokenizedEquityTimeInForce } from "~/v2/helpers/tokenizedEquityTrading";
import {
  amplitudeTradeTracking,
  type OrderEvent,
} from "~/v2/pages/dashboard/tradeV2/amplitudeTradeTracking";
import { concatFormErrorMessages } from "../shared/utils";
import { useAvailableQuantity } from "./hooks/useAvailableQuantity";
import { useBuyingPower } from "./hooks/useBuyingPower";
import { useEstimatedCost } from "./hooks/useEstimatedCost";
import { useEstimatedShares } from "./hooks/useEstimatedShares";
import { usePrice } from "./hooks/usePrice";
import {
  getTimeInForceOptions,
  useTimeInForceOptions,
} from "./hooks/useTimeInForceOptions";
import {
  type TrailingStopPrice,
  useTrailingStopPrice,
} from "./hooks/useTrailingStopPrice";
import {
  type EquityOrderTicketSchema,
  equityOrderTicketSchemaSuperRefinement,
  isTimeInForceValid,
  zEquityOrderTicketSchema,
} from "./schema";
import { createOrderObject, getIsSubmitDisabled } from "./utils";

const SUBMIT_COMPLETED_TIMEOUT = 500;

const getDefaultFormValues = (
  isTokenizedEquity: boolean,
  initialQuantity?: number,
): Partial<EquityOrderTicketSchema> => ({
  orderType: "market",
  timeInForce: isTokenizedEquity ? "gtc" : "day",
  quantityType: "shares",
  quantity: initialQuantity && initialQuantity > 0 ? initialQuantity : 1,
  trailType: "trail_price",
  isExtendedHours: false,
});

interface UseEquityOrderTicketControllerProps {
  account: Account | undefined;
  product: Product | undefined;
  asset: Asset | undefined;
  margin: Margin | undefined;
  quote: Quote | undefined;
  positions: Position[] | undefined;
  symbol: string;
  clock: Clock | undefined;
  initialQuantity?: number;
}

export const useEquityOrderTicketController = ({
  account,
  product,
  clock,
  asset,
  quote,
  margin,
  positions,
  symbol,
  initialQuantity,
}: UseEquityOrderTicketControllerProps): UseEquityOrderTicketControllerReturn => {
  // State for submit completed animation
  const [isSubmitCompleted, setIsSubmitCompleted] = useState(false);

  const isTokenizedEquityTradingEnabled = useFeatureEnabled(
    FeatureFlags.TOKENIZED_EQUITY_TRADING,
  );
  const isTokenizedEquity = shouldUseTokenizedEquityTimeInForce(
    symbol,
    isTokenizedEquityTradingEnabled,
  );

  const formResolver = useMemo(
    () =>
      zodResolver(
        zEquityOrderTicketSchema.superRefine((data, ctx) =>
          equityOrderTicketSchemaSuperRefinement(data, ctx, {
            isTokenizedEquity,
          }),
        ),
      ),
    [isTokenizedEquity],
  );

  // Review state
  const [reviewState, setReviewState] = useState<{
    isInReview: boolean;
    reviewSide: Side;
  }>({ isInReview: false, reviewSide: "buy" });

  const { marketStatus } = useGetMarketStatus();
  const isOvernight = marketStatus?.phase === "overnight";
  const isOvernightTradable =
    asset?.attributes?.includes("overnight_tradable") ?? false;

  const {
    reviewQuote,
    reviewFeedLabel,
    isReviewQuoteLoading,
    onRefreshReviewQuote,
  } = useOrderTicketReviewQuote({
    symbol,
    product,
    isOvernightTradable,
    isInReview: reviewState.isInReview,
  });

  // Form
  const {
    setValue,
    watch,
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<EquityOrderTicketSchema>({
    defaultValues: getDefaultFormValues(isTokenizedEquity, initialQuantity),
    mode: "onChange",
    resolver: formResolver,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: symbol is the trigger: the form is reset to defaults when the user switches asset. The body writes rather than reads it, which is why the rule cannot see the intent.
  useEffect(() => {
    resetForm(getDefaultFormValues(isTokenizedEquity, initialQuantity));
  }, [symbol, isTokenizedEquity, initialQuantity, resetForm]);

  const quantityType = watch("quantityType");
  const quantity = watch("quantity");
  const trailType = watch("trailType");
  const trailValue = watch("trailValue");
  const orderType = watch("orderType");
  const limitPrice = watch("limitPrice");
  const stopPrice = watch("stopPrice");
  const timeInForce = watch("timeInForce");
  const isExtendedHours = watch("isExtendedHours");

  const isNotional = quantityType === "dollars" && orderType === "market";
  const isFractional = !Number.isInteger(Number(quantity ?? 0));

  const orderEventData: OrderEvent = {
    order_type: orderType,
    asset_symbol: symbol,
    asset_class: "us_equity",
    is_notional: isNotional,
    qty: isNotional ? undefined : quantity,
    amount: isNotional ? quantity : undefined,
    time_in_force: timeInForce,
  };

  // Only market orders can be notional (quantityType in dollars)
  const isQuantityTypeDisabled = orderType !== "market";

  // Extended hours is only available for limit orders with day or gtc timeInForce
  const isExtendedHoursDisabled =
    isTokenizedEquity ||
    !(
      orderType === "limit" &&
      (timeInForce === "day" || timeInForce === "gtc")
    );

  const position = useMemo(
    () => positions?.find((position) => position.symbol === symbol),
    [positions, symbol],
  );

  const buyingPower = useBuyingPower({ asset, margin, isOvernight });

  const isNotTradable = !!asset && !asset.tradable;

  const isOvernightNotSupported =
    isOvernight &&
    !!asset &&
    asset.tradable &&
    !asset.attributes?.includes("overnight_tradable");

  const isOvernightHalted =
    isOvernight &&
    !!asset &&
    asset.tradable &&
    !isOvernightNotSupported &&
    (asset.attributes?.includes("overnight_halted") ?? false);

  const quoteLatestUpdated = useMemo(() => {
    if (!quote?.bid_timestamp) {
      return undefined;
    }

    // bid_timestamp comes from snap.latestQuote.t — the true quote timestamp
    return moment(quote.bid_timestamp)
      .tz("America/New_York")
      .format("MM/DD HH:mm:ss");
  }, [quote?.bid_timestamp]);

  const reviewQuoteLastUpdated = useMemo((): string => {
    if (!reviewQuote?.bid_timestamp) {
      return quoteLatestUpdated ?? "";
    }
    return moment(reviewQuote.bid_timestamp)
      .tz("America/New_York")
      .format("MM/DD HH:mm:ss");
  }, [reviewQuote?.bid_timestamp, quoteLatestUpdated]);

  const { sharesAvailable, dollarsAvailable } = useAvailableQuantity({
    position,
  });

  const { midPrice, priceChange } = usePrice({ quote });

  const { estimatedShares } = useEstimatedShares({
    isNotional,
    quantity,
    midPrice: midPrice.value,
    lastQuotePrice: quote?.last ?? 0,
  });

  const trailingStopPrice = useTrailingStopPrice({
    trailType,
    trailValue,
    lastQuotePrice: quote?.last ?? 0,
  });

  const { timeInForceOptions } = useTimeInForceOptions({
    orderType,
    quantityType,
    isFractional,
    clock,
    isExtendedHours,
    isTokenizedEquity,
  });

  const { estimatedCost } = useEstimatedCost({
    quantity,
    midPrice: midPrice.value,
    lastQuote: quote?.last ?? 0,
    isNotional,
    limitPrice,
    stopPrice,
  });

  const { midPrice: reviewMidPrice } = usePrice({ quote: reviewQuote });

  const { estimatedCost: reviewEstimatedCost } = useEstimatedCost({
    quantity,
    midPrice: reviewMidPrice.value,
    lastQuote: reviewQuote?.last ?? 0,
    isNotional,
    limitPrice,
    stopPrice,
  });

  // USed for side effect only
  const onQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isFractional = !Number.isInteger(Number(event.target.value));

    // timeInForce must be "day" for fractional orders (equity only)
    if (isFractional && !isTokenizedEquity) {
      setValue("timeInForce", "day");
    }
  };

  const onQuantityBlur: FocusEventHandler<HTMLInputElement> = (event) =>
    amplitudeTradeTracking.quantityChanged(event.target.value);

  const onIsExtendedHoursChange = (isExtendedHours: boolean) =>
    setValue("isExtendedHours", isExtendedHours);

  const onTrailTypeChange = (trailType: TrailType) =>
    setValue("trailType", trailType);

  const onTimeInForceChange = (timeInForce: TimeInForce) =>
    setValue("timeInForce", timeInForce);

  const onOrderTypeChange = (newOrderType: OrderType) => {
    setValue("orderType", newOrderType);
    amplitudeTradeTracking.orderTypeChanged(newOrderType);
    // Reset these values when order type changes
    setValue("limitPrice", undefined);
    setValue("stopPrice", undefined);

    const defaultTimeInForce = isTokenizedEquity ? "gtc" : "day";
    setValue("timeInForce", defaultTimeInForce);

    if (
      isTokenizedEquity &&
      !isTimeInForceValid({
        timeInForce,
        orderType: newOrderType,
        quantityType,
        quantity,
        isTokenizedEquity: true,
      })
    ) {
      const validOptions = getTimeInForceOptions({
        orderType: newOrderType,
        quantityType,
        isFractional,
        clock,
        isExtendedHours,
        isTokenizedEquity: true,
      });
      if (validOptions.length > 0) {
        setValue("timeInForce", validOptions[0].value);
      }
    }

    // Only market orders can be notional
    if (newOrderType !== "market") {
      setValue("quantityType", "shares");
    }
    // Only limit orders can be extended hours (not for tokenized equity)
    if (newOrderType !== "limit") {
      setValue("isExtendedHours", false);
    } else if (!isTokenizedEquity) {
      // Extended hours default on limit orders
      setValue("isExtendedHours", true);
    }
  };

  const onQuantityTypeChange = (quantityType: QuantityType) => {
    setValue("quantityType", quantityType);

    // timeInForce must be "day" for notional orders (equity only)
    if (quantityType === "dollars" && !isTokenizedEquity) {
      setValue("timeInForce", "day");
    }
  };

  const queryClient = useQueryClient();
  const invalidateOrderAndPositionQueries = () => {
    invalidateOrderRelatedQueries(queryClient);
  };

  const mutation = useMutationRequest(
    ({
      product,
      ...args
    }: EquityOrderTicketSchema & {
      side: Side;
      product: "paper" | "live";
      symbol: string;
    }) => createOrder(account?.id ?? "", createOrderObject(args), product),
    {
      onSuccess: () => {
        toastSuccess("Your order has been placed successfully");
        invalidateOrderAndPositionQueries();
        setIsSubmitCompleted(true);
        setReviewState((prev) => ({ ...prev, isInReview: false }));
        setTimeout(() => {
          setIsSubmitCompleted(false);
          resetForm(getDefaultFormValues(isTokenizedEquity));
        }, SUBMIT_COMPLETED_TIMEOUT);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Order failed: ${errorMessage}`);
      },
    },
  );

  const isSubmitDisabled = useMemo(
    () =>
      isSubmitCompleted ||
      getIsSubmitDisabled({
        account,
        asset,
        quote,
        product: product as "paper" | "live",
        quantity,
        quantityType,
        limitPrice,
        stopPrice,
        trailValue,
        orderType,
      }),
    [
      account,
      asset,
      quote,
      product,
      quantity,
      quantityType,
      limitPrice,
      stopPrice,
      trailValue,
      orderType,
      isSubmitCompleted,
    ],
  );

  const isSubmitLoading = mutation.isPending;

  // Buy/Sell buttons transition to review state (form validation still runs)
  const onValidReview =
    (side: Side): SubmitHandler<EquityOrderTicketSchema> =>
    () => {
      setReviewState({ isInReview: true, reviewSide: side });
      amplitudeTradeTracking.reviewOrderClicked(orderEventData);
    };

  const onInvalidSubmit =
    (_side: Side): SubmitErrorHandler<EquityOrderTicketSchema> =>
    (errors) =>
      toastError(`Invalid Order: ${concatFormErrorMessages(errors)}`);

  const onSubmit = (side: Side) =>
    handleSubmit(onValidReview(side), onInvalidSubmit(side));

  const onConfirmOrder = () => {
    handleSubmit(
      (data) => {
        if (
          !mutation.isPending &&
          (product === "live" || product === "paper")
        ) {
          mutation.mutate({
            ...data,
            side: reviewState.reviewSide,
            product,
            symbol,
          });
          amplitudeTradeTracking.confirmOrderClicked(orderEventData);
        }
      },
      (errors) =>
        toastError(`Invalid Order: ${concatFormErrorMessages(errors)}`),
    )();
  };

  const onEditClicked = () =>
    setReviewState((prev) => ({ ...prev, isInReview: false }));

  return {
    // Form control & submission
    control,
    onSubmit,
    isSubmitDisabled,
    isSubmitLoading,
    isSubmitCompleted,

    // Review state
    isInReview: reviewState.isInReview,
    reviewSide: reviewState.reviewSide,
    onConfirmOrder,
    onEditClicked,

    // Review quote (subscription-aware)
    reviewQuote,
    reviewQuoteLastUpdated,
    reviewFeedLabel,
    isReviewQuoteLoading,
    onRefreshReviewQuote,

    // Buying power
    buyingPower,

    // Price & quote
    midPrice,
    reviewMidPrice,
    priceChange,
    quoteLatestUpdated,
    estimatedCost,
    reviewEstimatedCost,
    estimatedShares,

    // Order type
    orderType,
    onOrderTypeChange,

    // Quantity
    quantity,
    quantityType,
    onQuantityChange,
    onQuantityBlur,
    onQuantityTypeChange,
    isQuantityTypeDisabled,

    // Time in force
    timeInForce,
    onTimeInForceChange,
    timeInForceOptions,

    // Extended hours
    isExtendedHours,
    onIsExtendedHoursChange,
    isExtendedHoursDisabled,

    // Trailing stop
    trailType,
    trailValue,
    onTrailTypeChange,
    trailingStopPrice,

    // Available quantity (for sells)
    sharesAvailable,
    dollarsAvailable,

    // Prices for review card
    limitPrice,
    stopPrice,

    // Overnight / trading status
    isOvernightNotSupported,
    isOvernightHalted,
    isNotTradable,
  };
};

export type UseEquityOrderTicketControllerReturn = {
  // Form control & submission
  control: Control<EquityOrderTicketSchema>;
  onSubmit: (side: Side) => (event?: BaseSyntheticEvent) => Promise<void>;
  isSubmitDisabled: boolean;
  isSubmitLoading: boolean;
  isSubmitCompleted: boolean;

  // Review state
  isInReview: boolean;
  reviewSide: Side;
  onConfirmOrder: () => void;
  onEditClicked: () => void;

  // Review quote (subscription-aware)
  reviewQuote: Quote | undefined;
  reviewQuoteLastUpdated: string;
  reviewFeedLabel: string;
  isReviewQuoteLoading: boolean;
  onRefreshReviewQuote: () => void;

  // Buying power
  buyingPower: { value: string; display: string; label: string };

  // Price & quote
  midPrice: { value: number; display: string };
  reviewMidPrice: { value: number; display: string };
  priceChange: { value: number; percentage?: string };
  quoteLatestUpdated: string | undefined;
  estimatedCost: string;
  reviewEstimatedCost: string;
  estimatedShares: string | undefined;

  // Order type
  orderType: OrderType;
  onOrderTypeChange: (orderType: OrderType) => void;

  // Quantity
  quantity: number;
  quantityType: QuantityType;
  onQuantityChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  onQuantityTypeChange: (quantityType: QuantityType) => void;
  isQuantityTypeDisabled: boolean;

  // Time in force
  timeInForce: TimeInForce;
  onTimeInForceChange: (timeInForce: TimeInForce) => void;
  timeInForceOptions: {
    label: string;
    subLabel?: string;
    value: TimeInForce;
  }[];

  // Extended hours
  isExtendedHours: boolean;
  onIsExtendedHoursChange: (isExtendedHours: boolean) => void;
  isExtendedHoursDisabled: boolean;

  // Trailing stop
  trailType: TrailType;
  trailValue: number | string | undefined;
  onTrailTypeChange: (trailType: TrailType) => void;
  trailingStopPrice: TrailingStopPrice;

  // Available quantity (for sells)
  sharesAvailable: number | undefined;
  dollarsAvailable: string | undefined;

  // Prices for review card
  limitPrice: number | undefined;
  stopPrice: number | undefined;

  // Overnight / trading status
  isOvernightNotSupported: boolean;
  isOvernightHalted: boolean;
  isNotTradable: boolean;
};
